// COP-11 — UTM capture + product analytics events.
//
// This module is the single client-side entry point for analytics. It is intentionally tiny:
// the event stream lives in the Supabase tables `signup_attribution` and `analytics_events`,
// and MarketingMX pulls funnel data via the `analytics-export` edge function (CSV).
//
// Design notes:
//   * `anon_id` — a per-browser UUID stored in localStorage. Survives across visits and is
//     linked to the Supabase auth user once signup completes (server-side via
//     `link_signup_attribution`).
//   * First-touch attribution is frozen on the very first landing with utm_* params and never
//     overwritten. Last-touch is overwritten on each fresh utm_*-tagged landing.
//   * Failures are swallowed — analytics MUST NOT break signup or any user-facing flow.

import { supabase } from '@/integrations/supabase/client';

// The auto-generated Database types in @/integrations/supabase/types do not yet include the
// COP-11 tables (`signup_attribution`, `analytics_events`) because they are regenerated from
// the live schema after the migration is applied. Until then, route writes through this
// untyped surface so the rest of the module stays strict.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

const ANON_ID_KEY        = 'fp_anon_id';
const FIRST_TOUCH_KEY    = 'fp_attr_first_touch';
const LAST_TOUCH_KEY     = 'fp_attr_last_touch';
const PERSISTED_FLAG_KEY = 'fp_attr_row_created';

export type EventName =
  | 'signup_started'
  | 'signup_completed'
  | 'trial_activated'
  | 'paid_converted'
  | 'subscription_canceled';

export interface UtmParams {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  landing_path?: string;
  referrer?: string;
  captured_at?: string; // ISO timestamp
}

export interface TrackProps {
  segment?: 'planner' | 'salon' | 'couple' | 'familia' | string;
  tier?: string;
  channel?: 'web' | 'whatsapp' | 'referral' | 'direct' | string;
  workspace_id?: string;
  mrr_mxn?: number;
  properties?: Record<string, unknown>;
}

// ---------- low-level helpers ----------

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function uuidv4(): string {
  // crypto.randomUUID is available in all evergreen browsers; fall back for old Safari.
  const c: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c?.randomUUID) return c.randomUUID();
  const buf = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
  }
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function getOrCreateAnonId(): string {
  const store = safeStorage();
  if (!store) return uuidv4(); // ephemeral — SSR/incognito fallback
  let anon = store.getItem(ANON_ID_KEY);
  if (!anon) {
    anon = uuidv4();
    try { store.setItem(ANON_ID_KEY, anon); } catch { /* quota: ignore */ }
  }
  return anon;
}

// ---------- UTM parsing ----------

export function parseUtmFromSearch(search: string, opts?: { referrer?: string; pathname?: string }): UtmParams | null {
  if (!search) return null;
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const utm: UtmParams = {
    source:   params.get('utm_source')   || undefined,
    medium:   params.get('utm_medium')   || undefined,
    campaign: params.get('utm_campaign') || undefined,
    content:  params.get('utm_content')  || undefined,
    term:     params.get('utm_term')     || undefined,
  };

  const hasAnyUtm = !!(utm.source || utm.medium || utm.campaign || utm.content || utm.term);
  if (!hasAnyUtm) return null;

  utm.landing_path = opts?.pathname;
  utm.referrer     = opts?.referrer;
  utm.captured_at  = new Date().toISOString();
  return utm;
}

// ---------- attribution persistence ----------

type StoredAttribution = UtmParams;

export function readFirstTouch(): StoredAttribution | null {
  return readJson<StoredAttribution>(FIRST_TOUCH_KEY);
}
export function readLastTouch(): StoredAttribution | null {
  return readJson<StoredAttribution>(LAST_TOUCH_KEY);
}

function readJson<T>(key: string): T | null {
  const store = safeStorage();
  if (!store) return null;
  const raw = store.getItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

function writeJson(key: string, value: unknown): void {
  const store = safeStorage();
  if (!store) return;
  try { store.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

/**
 * Persist a fresh UTM hit:
 *   * always overwrites last-touch
 *   * sets first-touch only if not already set
 */
export function recordAttribution(utm: UtmParams): { firstTouch: StoredAttribution; lastTouch: StoredAttribution } {
  const existingFirst = readFirstTouch();
  const firstTouch = existingFirst ?? utm;
  if (!existingFirst) writeJson(FIRST_TOUCH_KEY, firstTouch);
  writeJson(LAST_TOUCH_KEY, utm);
  return { firstTouch, lastTouch: utm };
}

// ---------- public API ----------

/**
 * Capture UTMs from the current URL and persist them locally + remotely.
 * Safe to call on every route mount; it short-circuits when there are no utm_* params
 * and a row already exists.
 */
export async function captureLandingAttribution(): Promise<void> {
  if (typeof window === 'undefined') return;
  const utm = parseUtmFromSearch(window.location.search, {
    referrer: document.referrer || undefined,
    pathname: window.location.pathname,
  });

  const anon_id = getOrCreateAnonId();
  const store = safeStorage();
  const rowAlreadyCreated = store?.getItem(PERSISTED_FLAG_KEY) === '1';

  // No new UTMs and we've already pushed a row before — nothing to do.
  if (!utm && rowAlreadyCreated) return;

  const { firstTouch, lastTouch } = utm
    ? recordAttribution(utm)
    : { firstTouch: readFirstTouch() ?? emptyTouch(), lastTouch: readLastTouch() ?? emptyTouch() };

  try {
    const row = {
      anon_id,
      first_touch_source:        firstTouch.source        ?? null,
      first_touch_medium:        firstTouch.medium        ?? null,
      first_touch_campaign:      firstTouch.campaign      ?? null,
      first_touch_content:       firstTouch.content       ?? null,
      first_touch_term:          firstTouch.term          ?? null,
      first_touch_landing_path:  firstTouch.landing_path  ?? null,
      first_touch_referrer:      firstTouch.referrer      ?? null,
      first_touch_at:            firstTouch.captured_at   ?? new Date().toISOString(),
      last_touch_source:         lastTouch.source         ?? null,
      last_touch_medium:         lastTouch.medium         ?? null,
      last_touch_campaign:       lastTouch.campaign       ?? null,
      last_touch_content:        lastTouch.content        ?? null,
      last_touch_term:           lastTouch.term           ?? null,
      last_touch_landing_path:   lastTouch.landing_path   ?? null,
      last_touch_referrer:       lastTouch.referrer       ?? null,
      last_touch_at:             lastTouch.captured_at    ?? new Date().toISOString(),
    };

    // Upsert on anon_id. ignoreDuplicates=false so last-touch fields keep getting refreshed.
    const { error } = await sb
      .from('signup_attribution')
      .upsert(row, { onConflict: 'anon_id', ignoreDuplicates: false });
    if (error) {
      console.warn('[analytics] signup_attribution upsert failed', error.message);
      return;
    }
    store?.setItem(PERSISTED_FLAG_KEY, '1');
  } catch (e) {
    console.warn('[analytics] captureLandingAttribution failed', e);
  }
}

function emptyTouch(): StoredAttribution {
  return { captured_at: new Date().toISOString() };
}

/**
 * Emit an analytics event. Quiet on failure.
 *
 * Server-side variants (`trial_activated`, `paid_converted`, `subscription_canceled`) should
 * be fired by the billing edge function (COP-10) using the service-role key — calling them
 * from the client here is acceptable for early validation but billing is the source of truth.
 */
export async function track(eventName: EventName, props: TrackProps = {}): Promise<void> {
  try {
    const anon_id = getOrCreateAnonId();
    const lastTouch = readLastTouch();
    const firstTouch = readFirstTouch();

    let auth_user_id: string | null = null;
    try {
      const { data } = await supabase.auth.getUser();
      auth_user_id = data?.user?.id ?? null;
    } catch { /* not authed */ }

    const row = {
      event_name:           eventName,
      anon_id,
      auth_user_id,
      workspace_id:         props.workspace_id ?? null,
      segment:              props.segment ?? null,
      tier:                 props.tier ?? null,
      attribution_source:   lastTouch?.source ?? firstTouch?.source ?? null,
      attribution_campaign: lastTouch?.campaign ?? firstTouch?.campaign ?? null,
      attribution_medium:   lastTouch?.medium ?? firstTouch?.medium ?? null,
      channel:              props.channel ?? 'web',
      mrr_mxn:              typeof props.mrr_mxn === 'number' ? props.mrr_mxn : null,
      properties:           props.properties ?? {},
    };

    const { error } = await sb.from('analytics_events').insert(row);
    if (error) {
      console.warn(`[analytics] event ${eventName} insert failed`, error.message);
    }
  } catch (e) {
    console.warn(`[analytics] track(${eventName}) threw`, e);
  }
}

/**
 * Link the anon attribution row to the Supabase auth user post-signup.
 * Idempotent. Calls the SECURITY DEFINER `link_signup_attribution` RPC server-side.
 */
export async function linkAttributionToUser(authUserId: string): Promise<void> {
  try {
    const anon_id = getOrCreateAnonId();
    const { error } = await sb.rpc('link_signup_attribution', {
      p_anon_id: anon_id,
      p_auth_user_id: authUserId,
    });
    if (error) console.warn('[analytics] linkAttributionToUser failed', error.message);
  } catch (e) {
    console.warn('[analytics] linkAttributionToUser threw', e);
  }
}

// ----- typed event helpers (callers should prefer these) -----

export const trackSignupStarted        = (p?: TrackProps) => track('signup_started', p);
export const trackSignupCompleted      = (p?: TrackProps) => track('signup_completed', p);
export const trackTrialActivated       = (p?: TrackProps) => track('trial_activated', p);
export const trackPaidConverted        = (p?: TrackProps) => track('paid_converted', p);
export const trackSubscriptionCanceled = (p?: TrackProps) => track('subscription_canceled', p);
